import { GetStaticProps, InferGetStaticPropsType, GetStaticPaths } from 'next';

import { PostListPage } from '@components';
import { getMetaDescription } from '@helpers';
import { categoryPath, basePath } from '@paths';
import {
  hnaviShortTitle,
  hnaviTitle,
  interviewBasePath,
  shortTitle,
  title,
} from '@constants';
import { getPostCountByAllCategories, getRedirectPaths } from '@api';
import { calcPageCountFromPostCount } from '@utils';
import { useCanonical } from '@hooks';
import { getStaticPropsCore } from '@/src/getStatic/categoryGetStaticPropsCore';

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  categorySlug,
  hnaviCategory,
  categoryInfo,
  ...rest
}) => {
  const isInterview = categorySlug === 'interview';
  return (
    <PostListPage
      {...rest}
      pageCount={calcPageCountFromPostCount(
        rest.posts?.pageInfo?.offsetPagination?.total ?? 0
      )}
      seoData={{
        ...categoryInfo?.seo,
        canonical: useCanonical(),
        metaDesc:
          categoryInfo?.seo?.metaDesc ||
          getMetaDescription({
            category: { name: categoryInfo.name, slug: categorySlug },
          }),
      }}
      pageTitle={categoryInfo?.name}
      breadcrumbs={
        isInterview
          ? [
              {
                text: hnaviTitle,
                shortText: hnaviShortTitle,
                url: '/',
              },
              {
                text: 'インタビュー',
                url: interviewBasePath,
              },
            ]
          : [
              {
                text: title,
                shortText: shortTitle,
                url: basePath,
              },
              {
                text: categoryInfo?.name,
                url: categoryPath(categorySlug),
              },
            ]
      }
      paginationBasePath={
        isInterview ? interviewBasePath : categoryPath(categorySlug)
      }
    />
  );
};

export default Page;

export const getStaticProps = async ({
  params,
}: Parameters<
  GetStaticProps<any, { slug: string /* カテゴリーslug */; pageNo: string }>
>[0]) => {
  console.info(`getStaticProps /category/${params?.slug}/${params?.pageNo}`);
  return await getStaticPropsCore(params?.slug, {
    pageNo: params?.pageNo,
    redirectionReferencePath: `/knowledge/category/${params?.slug}/`,
  });
};

type PathParams = {
  slug: string;
  pageNo: string;
};

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  const redirects = await getRedirectPaths();
  const postCountByAllCategories = (await getPostCountByAllCategories()) || [];

  return {
    paths: postCountByAllCategories
      .reduce<PathParams[]>((acc, { slug, postCount }) => {
        if (!slug) {
          return acc;
        }
        if (redirects.has(`/knowledge/category/${slug}/`)) {
          return acc;
        }
        return [
          ...acc,
          ...Array(calcPageCountFromPostCount(postCount))
            .fill(0)
            .map<PathParams>((_, i) => ({ slug, pageNo: String(1 + i) })),
        ];
      }, [])
      .map((params) => ({ params })),
    fallback: 'blocking',
  };
};
